import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { NotificationManager } from 'react-notifications';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import InfoBox from '../../components/shared/InfoBox/InfoBox';
import BtButton from '../bt-button/bt-button';
import { errorKeyOf } from '../app-evaluation-start/evaluationStart';
import SupplierEvaluationSummary from '../app-supplier-database/SupplierEvaluationSummary';
import { reportUnexpected } from '../page-supplier-evaluations/reportUnexpected';
import { formatShortDay } from '../page-supplier-evaluations/reviewFormat';
import {
  EvaluationTagDto,
  OverviewApiQuery,
  OverviewMetrics,
  serviceEvaluations,
} from '../../services/service-evaluations';
import appState from '../../state/AppStateContainer';
import { EvaluationErrors } from '../../util/evaluationContract';
import { readEvaluations } from '../../util/evaluationsCache';
import {
  EvaluationsView,
  WizardSeed,
  evaluationsUrl,
  openWizard,
  rememberEvaluationsView,
  wizardUrl,
} from '../../util/evaluationsNavigation';
import {
  OverviewFilters,
  OverviewQuery,
  OverviewSort,
  clearFilters,
  filterKeyOf,
  hasFilter,
  parseOverviewQuery,
  toSearch,
  withFilter,
  withSort,
} from './overviewQuery';
import { OverviewMeta } from './remoteTable';
import OverviewTiles from './OverviewTiles';
import OverviewToolbar from './OverviewToolbar';
import ProjectGroupTable from './ProjectGroupTable';
import SupplierOverviewTable from './SupplierOverviewTable';
import styles from './app-evaluation-overview.module.scss';

export type EvaluationsOverviewProps = { view: EvaluationsView };

const SEARCH_DEBOUNCE_MS = 300;

const isMigrationPending = (error: unknown) =>
  errorKeyOf(error) === EvaluationErrors.MigrationPending;

const isNotFound = (error: unknown) => axios.isAxiosError(error) && error.response?.status === 404;

type LoadedPage = OverviewMeta & { view: EvaluationsView; query: OverviewQuery; tableKey: number };

const isFirstUse = (page: LoadedPage) =>
  page.totalCount === 0 &&
  !hasFilter(page.query) &&
  page.counts.all === 0 &&
  page.counts.unevaluated === 0;

export const EvaluationsOverview: FC<EvaluationsOverviewProps> = ({ view }) => {
  const [t, i18n] = useTranslation('evaluations');
  const history = useHistory();
  const location = useLocation();
  const query = useMemo(() => parseOverviewQuery(location.search), [location.search]);
  const queryRef = useRef(query);
  queryRef.current = query;
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const searchRef = useRef(location.search);
  searchRef.current = location.search;

  const [metrics, setMetrics] = useState<OverviewMetrics | null>(null);
  const [tags, setTags] = useState<EvaluationTagDto[]>([]);
  const [loaded, setLoaded] = useState<LoadedPage | null>(null);
  const [failedView, setFailedView] = useState<EvaluationsView | null>(null);
  const [migrationPending, setMigrationPending] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const tableKeyRef = useRef(tableKey);
  tableKeyRef.current = tableKey;
  // What is typed and not yet in the URL; the field shows the URL's search otherwise.
  const [draft, setDraft] = useState<string | null>(null);
  const canWrite = appState.isWriteAccessRole();
  const meta = loaded?.view === view ? loaded : null;
  // Empty and first use speak for the query on screen only once its page has arrived.
  const settled = meta && filterKeyOf(meta.query) === filterKeyOf(query) ? meta : null;
  const failed = failedView === view;

  const replaceQuery = useCallback(
    (next: OverviewQuery) => history.replace({ pathname: pathRef.current, search: toSearch(next) }),
    [history],
  );

  // Tiles ignore every filter and the view, so they load once per mount.
  useEffect(() => {
    const read = readEvaluations('metrics', null, () => serviceEvaluations.getMetrics());
    if (read.cached) setMetrics(read.cached);
    read.fresh
      .then(({ value, changed }) => changed && setMetrics(value))
      .catch(error => {
        if (read.cached) return;
        if (isMigrationPending(error)) setMigrationPending(true);
        else reportUnexpected(error);
      });
  }, []);

  useEffect(() => {
    const read = readEvaluations('tags', null, () => serviceEvaluations.getTags());
    if (read.cached) setTags(read.cached);
    read.fresh
      .then(({ value, changed }) => changed && setTags(value))
      .catch(error => !read.cached && reportUnexpected(error));
  }, []);

  useEffect(() => {
    if (draft === null) return undefined;
    const timer = window.setTimeout(() => {
      if (draft !== queryRef.current.q) replaceQuery(withFilter(queryRef.current, { q: draft }));
      setDraft(null);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, replaceQuery]);

  const onFilter = (change: Partial<OverviewFilters>) =>
    replaceQuery(withFilter(queryRef.current, change));

  const focusId = view === 'supplier' ? query.supplier : null;
  const focusName = focusId && meta?.focus?.supplierId === focusId ? meta.focus.name : null;

  // The focus shows as its supplier's name in the search field; the first edit drops it.
  const onSearch = (value: string) => {
    if (queryRef.current.supplier) replaceQuery(withFilter(queryRef.current, { supplier: null }));
    setDraft(value);
  };

  useEffect(() => rememberEvaluationsView(view), [view]);

  const onViewChange = (next: EvaluationsView) => {
    if (next === view) return;
    const kept = { ...queryRef.current, supplier: null, open: null, page: 1, sort: null };
    history.push(evaluationsUrl(next === 'supplier' ? '/by-supplier' : '') + toSearch(kept));
  };

  const onLoaded = useCallback(
    (page: OverviewMeta, loadedQuery: OverviewQuery) => {
      setFailedView(null);
      setLoaded({ ...page, view, query: loadedQuery, tableKey: tableKeyRef.current });
    },
    [view],
  );

  const onFailed = useCallback(
    (error: unknown, apiQuery: OverviewApiQuery) => {
      if (isMigrationPending(error)) {
        setMigrationPending(true);
        return;
      }
      // A shared or stale focus link must not leave the page on a retry that fails forever.
      if (apiQuery.supplierId && isNotFound(error)) {
        replaceQuery({ ...queryRef.current, supplier: null, open: null, page: 1 });
        NotificationManager.info(
          t('overview.focusUnavailable', 'Leverantören kunde inte visas. Visar alla leverantörer.'),
        );
        return;
      }
      reportUnexpected(error);
      setFailedView(view);
    },
    [replaceQuery, t, view],
  );

  const onOpenChange = useCallback(
    (open: string | null) => replaceQuery({ ...queryRef.current, open }),
    [replaceQuery],
  );

  const onPageChange = useCallback(
    (page: number) => replaceQuery({ ...queryRef.current, page, open: null }),
    [replaceQuery],
  );

  const onPageRestored = useCallback(
    (page: number) => replaceQuery({ ...queryRef.current, page }),
    [replaceQuery],
  );

  const onSortChange = useCallback(
    (sort: OverviewSort | null) => replaceQuery(withSort(queryRef.current, sort)),
    [replaceQuery],
  );

  const onStart = useCallback(
    (seed: WizardSeed) => openWizard(history, seed, pathRef.current + searchRef.current),
    [history],
  );

  const retry = () => {
    setFailedView(null);
    setTableKey(key => key + 1);
  };

  const from = location.pathname + location.search;

  if (migrationPending) {
    return (
      <div className={styles.overview}>
        <InfoBox
          description={t(
            'errors.migrationPending',
            'Utvärderingar förbereds. Försök igen om en stund.',
          )}
        />
      </div>
    );
  }

  const isEmpty = settled !== null && settled.totalCount === 0;
  const firstUse = settled !== null && isFirstUse(settled);
  // Before the first page of a view or a remounted table, or after an empty page, it says none.
  const loading =
    !failed &&
    (meta === null || meta.tableKey !== tableKey || (settled === null && meta.totalCount === 0));
  // The filter row stays through loading and a view switch; only first use has nothing to filter.
  const showFilters = loaded === null || !isFirstUse(loaded);
  const loadedBatch = (meta ?? loaded)?.batch;
  const shownBatch = loadedBatch?.batchId === query.batch ? loadedBatch : null;

  const batchLabel = () => {
    if (!query.batch) return null;
    if (!shownBatch) return t('overview.pill.batchUnknown', 'Utskick');
    return shownBatch.title
      ? t('overview.pill.batch', 'Utskick: {{title}}', { title: shownBatch.title })
      : t('overview.pill.batchDated', 'Utskick {{date}}', {
          date: formatShortDay(shownBatch.createdDay, i18n.language),
        });
  };

  // A focus with no other filter is the supplier's profile, which an empty list must not blank.
  const emptyFocus =
    isEmpty && !hasFilter({ ...query, supplier: null }) ? (settled?.focus ?? null) : null;

  const filteredEmptyText = () => {
    if (emptyFocus) {
      return t(
        'overview.focusEmpty',
        '{{name}} har inga utvärderingar som du har behörighet att se.',
        { name: emptyFocus.name },
      );
    }
    return view === 'project'
      ? t('overview.filteredEmpty.project', 'Inga projekt matchar filtret.')
      : t('overview.filteredEmpty.supplier', 'Inga leverantörer matchar filtret.');
  };

  const tableProps = {
    query,
    canWrite,
    onLoaded,
    onFailed,
    onOpenChange,
    onPageChange,
    onPageRestored,
    onSortChange,
    onStart,
  };

  return (
    <div className={styles.overview}>
      <OverviewTiles metrics={metrics} />
      <section className={styles.card}>
        <OverviewToolbar
          view={view}
          onViewChange={onViewChange}
          showFilters={showFilters}
          query={query}
          counts={(meta ?? loaded)?.counts ?? null}
          tags={tags}
          onFilter={onFilter}
          search={focusName ?? draft ?? query.q}
          onSearch={onSearch}
          batchLabel={batchLabel()}
        />
        {failed && (
          <div className={styles.loadError} role="alert">
            <span>{t('overview.loadFailed', 'Utvärderingarna kunde inte hämtas.')}</span>
            <BtButton color="white" size="xxs" tabIndex={0} onClick={retry}>
              {t('review.retry', 'Försök igen')}
            </BtButton>
          </div>
        )}
        {!failed && firstUse && (
          <div className={styles.empty}>
            <FolderOpenOutlinedIcon className={styles.emptyIcon} style={{ fontSize: 32 }} />
            <h3 className={styles.emptyTitle}>
              {t('overview.empty.title', 'Inga utvärderingar än')}
            </h3>
            <p className={styles.emptyText}>
              {view === 'project'
                ? t(
                    'overview.empty.project',
                    'Här samlas dina projekt med leverantörerna som vann upphandlingarna. Skicka en utvärdering efter ett projekt, eller för arbete som gjordes innan leverantören fanns i Accurator, och följ vem som har svarat.',
                  )
                : t(
                    'overview.empty.supplier',
                    'Här samlas varje leverantör ni har utvärderat, med pågående och avslutade utvärderingar. Skicka en utvärdering efter ett projekt för att komma igång.',
                  )}
            </p>
            {canWrite && (
              <BtButton
                color="white"
                tabIndex={0}
                onClick={() => history.push(wizardUrl(), { from })}
              >
                {t('register.newEvaluation', 'Ny utvärdering')}
              </BtButton>
            )}
          </div>
        )}
        {!failed && emptyFocus && (
          <div className={styles.focusSummary}>
            <SupplierEvaluationSummary
              supplierId={emptyFocus.supplierId}
              readOnly={false}
              onStartEvaluation={
                canWrite
                  ? () =>
                      onStart({
                        project: null,
                        suppliers: [{ _id: emptyFocus.supplierId, name: emptyFocus.name }],
                      })
                  : undefined
              }
            />
          </div>
        )}
        {!failed && isEmpty && !firstUse && (
          <div className={styles.filteredEmpty}>
            <span className={styles.muted}>{filteredEmptyText()}</span>
            {hasFilter(query) && (
              <button
                type="button"
                className={styles.quietAction}
                onClick={() => replaceQuery(clearFilters(queryRef.current))}
              >
                {t('overview.clearFilters', 'Rensa filter')}
              </button>
            )}
          </div>
        )}
        {loading && (
          <p className={`${styles.loading} ${styles.muted}`}>
            {t('panel.loading', 'Hämtar utvärderingar…')}
          </p>
        )}
        <div className={styles.table} hidden={failed || isEmpty || loading}>
          {view === 'project' ? (
            <ProjectGroupTable key={tableKey} {...tableProps} />
          ) : (
            <SupplierOverviewTable key={tableKey} {...tableProps} />
          )}
        </div>
      </section>
    </div>
  );
};

export default EvaluationsOverview;
